import {
  createUserWithEmailAndPassword, getAuth, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut,
} from 'firebase/auth'
import {
  doc, getDoc, getFirestore, setDoc,
} from 'firebase/firestore'
import {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react'

const SessionContext = createContext()

export const SessionProvider = (props) => {
  const { children } = props
  const [user, setUser] = useState(null)
  const [checkAuth, setCheckAuth] = useState(true)
  const auth = getAuth()
  const db = getFirestore()

  const loadMe = useCallback(
    async (uid) => {
      const docRef = doc(db, 'users', uid)
      const docSnap = await getDoc(docRef)
      return docSnap.data()
    }, [db],
  )

  useEffect(
    () => {
      setCheckAuth(true)
      onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser) {
          const { uid } = currentUser
          const userInfo = await loadMe(uid)
          const userData = {
            uid,
            role: userInfo.role,
          }
          setUser(userData)
        }
        setCheckAuth(false)
      })
    }, [auth, loadMe],
  )

  const login = useCallback(
    async ({ email, password }) => {
      try {
        const { user: currentUser } = await signInWithEmailAndPassword(auth, email, password)
        const userInfo = await loadMe(currentUser.uid)
        const userData = {
          uid: currentUser.uid,
          role: userInfo.role,
        }
        setUser(userData)
      } catch (err) {
        const errorMessage = {
          'auth/user-not-found': 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
          'auth/wrong-password': 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
        }
        throw errorMessage[err.code] || 'ขออภัยระบบมีปัญหา กรุณาติดต่อแอดมิน'
      }
    }, [auth, loadMe],
  )

  const logout = useCallback(
    async () => {
      try {
        await signOut(auth)
        setUser(null)
      } catch (err) {
        throw new Error(err)
      }
    }, [auth],
  )

  const register = useCallback(
    async ({ email, password }) => {
      try {
        const { user: currentUser } = await createUserWithEmailAndPassword(auth, email, password)
        await setDoc(doc(db, 'users', currentUser.uid), {
          uid: currentUser.uid,
          role: 'editor',
          email,
        })
      } catch (err) {
        const errorMessage = {
          'auth/weak-password': 'รหัสผ่านมีความปลอดภัยต่ำ กรุณาตั้งใหม่',
          'auth/email-already-in-use': 'อีเมลนี้ถูกใช้งานแล้ว',
        }
        throw errorMessage[err.code] || 'ขออภัยระบบมีปัญหา กรุณาติดต่อแอดมิน'
      }
    }, [auth, db],
  )

  const resetPassword = useCallback(
    async ({ email }) => {
      try {
        return sendPasswordResetEmail(auth, email)
      } catch (err) {
        throw new Error(err)
      }
    }, [auth],
  )

  return (
    <SessionContext.Provider
      value={{
        login,
        logout,
        register,
        resetPassword,
        user,
        checkAuth,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => useContext(SessionContext)

export default SessionContext
